import type { ConversationDisplayCopy } from "@/lib/i18n/conversation-copy"

/**
 * The vocabulary of the strip above a conversation — who, what state, and what
 * can be done about it.
 *
 * Here rather than in `lib/inbox/`, because three things read it that are not the
 * unified inbox: the shared header component, the confirmation dialog, and the
 * message agent's own inbox. `lib/conversation/` is where this repository already
 * keeps what both conversation surfaces share; a shared component reaching into
 * one domain's type module for its own contract is the coupling that rule exists
 * to prevent.
 */

import type { Permission } from "@/lib/auth/permissions.types"
import type {
  EscalationCategory,
  HumanOutcome,
  OutcomeSource,
  SessionOutcome,
} from "@/lib/conversation-quality/outcomes"

/** One colour vocabulary for state badges and the banner, so they cannot drift apart. */
export type ThreadTone = "neutral" | "info" | "success" | "warning"

/** One thing known about the conversation, already formatted for display. */
export interface ThreadFact {
  /** App-owned display keys; cached data and actor permissions stay locale-independent. */
  copy?: ConversationDisplayCopy
  label: string
  value: string
  /** `ChannelIcon` key. A key rather than a node: this crosses a server-action boundary. */
  icon?: string
  /** Its own full-width row, wrapping. For a value no pill can hold — a call summary. */
  block?: boolean
}

/** Something the conversation IS. Never pressable — see `ThreadAction` for that. */
export interface ThreadBanner {
  /** App-owned display keys; cached data and actor permissions stay locale-independent. */
  copy?: ConversationDisplayCopy
  tone: ThreadTone
  text: string
}

export interface ThreadStateBadge {
  /** App-owned display keys; cached data and actor permissions stay locale-independent. */
  copy?: ConversationDisplayCopy
  label: string
  tone: ThreadTone
}

/**
 * Something that changes the conversation, named by the subsystem that owns it.
 * A name appears here once and means one thing, so the client can map it to a
 * handler without also branching on the source.
 */
export const THREAD_ACTION_IDS = [
  "pause_ai",
  "resume_ai",
  "exempt_auto_pause",
  "clear_auto_pause_exemption",
  "exclude_contact",
  "include_contact",
  "new_session",
  "blacklist_sender",
  "unblacklist_sender",
  "classify_message",
  "generate_ai_reply",
  "create_crm_lead",
  "qa_feedback",
] as const
export type ThreadActionId = (typeof THREAD_ACTION_IDS)[number]

/**
 * Every action but one writes through a route. `qa_feedback` opens the agent's
 * reply for review on the client instead, so the mutation never sees it.
 */
export type ThreadMutationId = Exclude<ThreadActionId, "qa_feedback">
export type ThreadMutation = ThreadAction & { id: ThreadMutationId }

export function isThreadMutation(action: ThreadAction): action is ThreadMutation {
  return action.id !== "qa_feedback"
}

/**
 * A serializable icon key, not a component: the descriptor is built in a query
 * file and crosses a server-action boundary, so nothing renderable can travel in
 * it. The client maps key → component, and an unmapped key fails to compile
 * rather than rendering a blank button.
 *
 * A key names a glyph, not a press: `ai-active` is the button you press to pause,
 * because what it shows is what the AI is doing right now. Two actions may share
 * a key — blacklisting a sender and excluding a contact are different things
 * wearing the same crossed-out person.
 */
export const THREAD_ACTION_ICONS = [
  "ai-active",
  "ai-paused",
  "shield",
  "shield-off",
  "exclude",
  "include",
  "new-session",
  "classify",
  "draft",
  "crm",
  "feedback",
] as const
export type ThreadActionIcon = (typeof THREAD_ACTION_ICONS)[number]

/**
 * `available` — offered, and nothing has happened yet. `active` — the state this
 * action produces is already in force, so it reads as a toggle that is on.
 * `done` — it has already been performed and running it again would repeat work.
 * `blocked` — supported here, but not possible yet; `hint` says what is missing.
 *
 * The last two render the same way and mean different things. Both stay on screen
 * rather than vanishing: a button that disappears takes its explanation with it.
 */
export type ThreadActionState = "available" | "active" | "done" | "blocked"

/**
 * A descriptor, never a callback. The source says what this conversation
 * supports and what is already true; the client owns how it is performed, and
 * the service removes anything the caller may not do.
 */
export interface ThreadAction {
  /** App-owned display keys; cached data and actor permissions stay locale-independent. */
  copy?: ConversationDisplayCopy
  /**
   * `danger` where an active state means the agent is stopped for this contact.
   * Without it every active state reads alike, and a blacklist looks like a
   * preference.
   */
  tone?: "danger"

  id: ThreadActionId
  label: string
  icon: ThreadActionIcon
  state: ThreadActionState
  /** The permission of the subsystem this action acts on, as its route enforces it. */
  permission: Permission
  /** What pressing it does, where the label alone does not say — the button's tooltip. */
  hint?: string
  /**
   * Render with its label rather than as a bare icon. For the one control whose
   * current state the operator is meant to read at a glance, not hunt for.
   */
  prominent?: boolean
  /** The question to ask first, where the action is worth asking about. */
  confirm?: string
}

/**
 * Who, where, what state, and what can be done about it — the strip above the
 * transcript, in both inboxes.
 *
 * `facts` is the flat, already-formatted list the metadata pills render; the rest
 * is what a list of label/value pairs could not express.
 */
export interface ThreadHeader {
  facts: ThreadFact[]
  /** Under the title, beside the avatar: what identifies the contact, not the conversation. */
  identityLines?: string[]
  state: ThreadStateBadge[]
  actions: ThreadAction[]
  /** One line under the header, where a state needs more than a badge to explain it. */
  /** Several can be true at once: a blacklisted sender whose thread a colleague answered. */
  banners?: ThreadBanner[]
  /**
   * What the actions act ON, where the conversation id is not enough. An email
   * agent works per message and per sender, not per thread, so its routes need
   * both; a messaging conversation needs neither and leaves this absent.
   */
  target?: ThreadActionTarget
  /** Audio belonging to the conversation. Only a call has any. */
  recording?: ThreadRecording
  /**
   * An AI reply that was drafted and never sent.
   *
   * Offered, never loaded: the composer stays empty until the operator asks for
   * it, because sending text they did not write under their own name is what the
   * attribution rules elsewhere in this product exist to prevent.
   */
  pendingDraft?: { text: string }
  /** How the last judged session went. Only the messaging source fills it. */
  quality?: ThreadQuality
}

/**
 * The verdict on one finished session, as the thread badge and its correction
 * dialog need it.
 *
 * Values, not renderings: the labels and tones live in one client-safe module
 * (`lib/conversation-quality/outcome-display.ts`) and every surface reads them
 * from there, so a label cannot say one thing here and another in the analytics.
 *
 * `escalated` and `humanReplied` are recorded facts rather than the verdict, and they are
 * what decides which fields a correction must supply. `humanReplied` is deliberately not
 * `human_picked_up`: that column answers the narrower «did anyone answer *after* an
 * escalation», which is false on an unprompted takeover where a colleague plainly replied.
 */
export interface ThreadQuality {
  sessionId: string
  /** What the thread's own «Session #N» markers call it. */
  sessionNumber: number
  outcome: SessionOutcome
  outcomeSource: OutcomeSource
  escalationCategory: EscalationCategory | null
  humanOutcome: HumanOutcome | null
  escalated: boolean
  /** A person wrote in this session at all — the question a human outcome answers. */
  humanReplied: boolean
  /** The verdict is worth a person's confirmation — low confidence, or a correction to re-check. */
  needsReview: boolean
  summary: string | null
}

/**
 * A recording, as stored. The reference is passed through untouched — `storage://`
 * and all — because turning it into something playable means a signed URL, and
 * the player already knows how to ask for one.
 */
export interface ThreadRecording {
  url: string
  /** The provider's own id for the conversation, which the player labels it with. */
  conversationId: string
  /** The call this belongs to; the signed-URL endpoint is addressed by it. */
  callId: number
  /** Known length, so the player can show it before the audio has loaded. */
  durationSeconds?: number
}

export interface ThreadActionTarget {
  /** The message the per-message actions apply to — the newest inbound one. */
  messageId?: number
  /** The same message as its provider knows it; the email routes address it this way. */
  messageExternalId?: string
  /** The address a blacklist entry names, already resolved through any override. */
  senderEmail?: string
  /** The agent's newest reply on the thread — the row QA feedback attaches to. */
  autoReplyId?: string
}
